import { z } from 'zod';
import { zId, zPermission } from './primitives.js';

/**
 * RL-02, RL-04, RL-06, SA-04…SA-06. Defined once and used by the NestJS route,
 * the React form and the tests, so a field cannot mean one thing on each side.
 */

/** A role code is a slug: stable, referenced by seeds and fixtures. */
export const zRoleCode = z.string().min(2).max(48).regex(/^[a-z][a-z0-9_]*$/);

/**
 * RL-07 — a percentage, carried as a decimal string like every other amount.
 *
 * Exported as a pattern as well as a schema because the API's pure rule modules validate the
 * same thing without zod — `consignment/budget.ts` guards BG-04's tolerance band with it. One
 * declaration, two consumers: a second regex saying almost the same thing is how `advancePct`
 * came to accept four fractional digits on one side and two on the other.
 */
export const PERCENT_PATTERN = /^\d{1,3}(\.\d{1,4})?$/;

/**
 * The whole rule, as a predicate: well formed AND not above 100.
 *
 * The pattern alone admits '101' — three digits is three digits — so a caller that guards on the
 * regex and stops there is enforcing a different rule from the schema. `isPositiveAmount` exists
 * for the same reason, and this project has had four figures whose two definitions disagreed.
 */
export const isPercent = (s: string): boolean => PERCENT_PATTERN.test(s) && Number(s) <= 100;

export const zPercent = z
  .string()
  .regex(PERCENT_PATTERN, 'must be a decimal percentage with at most 4 fractional digits')
  .refine(isPercent, 'cannot exceed 100');

/**
 * RL-05 — which application a role is built for. Staff and trade customers hold
 * roles from the same table, and the permission catalog is partitioned the same
 * way: `order.view` exists on both sides and means different things. A role must
 * declare its side so the builder cannot grant it a permission nothing on that
 * side enforces.
 */
export const zAudience = z.enum(['INTERNAL', 'RETAILER']);

export const zCreateRole = z.object({
  code: zRoleCode,
  name: z.string().min(2).max(80),
  audience: zAudience.default('INTERNAL'),
  permissions: z.array(zPermission).default([]),
  portalIds: z.array(zId).default([]),
  pageIds: z.array(z.string().max(16)).default([]),
  discountCeilingPct: zPercent.nullable().optional(),
});

export const zUpdateRole = z.object({
  name: z.string().min(2).max(80).optional(),
  isActive: z.boolean().optional(),
  discountCeilingPct: zPercent.nullable().optional(),
}).refine((o) => Object.keys(o).length > 0, 'no fields to update');

export const zSetPermissions = z.object({ permissions: z.array(zPermission) });
export const zSetPortals = z.object({ portalIds: z.array(zId) });

/**
 * RL-05, CR-51 — which screens a role opens. Page IDs, not routes: a route can
 * be renamed in a release, and the grant must survive that. The catalog itself
 * stays in code (`access/pages.ts`); only the grant is data.
 */
export const zSetPages = z.object({ pageIds: z.array(z.string().max(16)) });

/** A role is deleted only deliberately, so the reason goes to the audit log. */
export const zDeleteRole = z.object({ reason: z.string().min(3).max(500).optional() });

export const zCreateUser = z.object({
  email: z.string().email().max(254),
  name: z.string().min(2).max(120),
  /**
   * Temporary, until the invite flow lands with Notifications in Sprint 8.
   * Long enough that a seeded account is not the way in.
   */
  password: z.string().min(12).max(200),
  roleIds: z.array(zId).default([]),
});

export const zUpdateUser = z.object({
  name: z.string().min(2).max(120).optional(),
  email: z.string().email().max(254).optional(),
}).refine((o) => Object.keys(o).length > 0, 'no fields to update');

/**
 * An administrator setting someone else's password, which is the only recovery
 * route until P-04/P-05 land. Same floor as creation — the audit entry records
 * that it happened and by whom, never the value.
 */
export const zSetUserPassword = z.object({ password: z.string().min(12).max(200) });

export const zSetUserRoles = z.object({ roleIds: z.array(zId) });

/** A suspension is a decision, so it carries a reason into the audit log (AU-01). */
export const zSuspendUser = z.object({ reason: z.string().min(3).max(500).optional() });

/*
 * A-06's retailer half. Deliberately NARROWER than `zUpdateUser`.
 *
 * `email` is absent, and that is the point: a retailer login's email is the identity the
 * customer signs in with and the address every notification about their account goes to, so
 * changing it from the Miraki side would silently redirect their mail and lock them out of a
 * login they still believe they hold. A customer who needs a different address asks their own
 * Owner, who holds that under RL-13. Support renames and recovers; it does not re-identify.
 */
export const zUpdateRetailerUser = z.object({
  /*
   * The parts, with the whole composed from them by the service. Same arrangement as ON-02's
   * application: callers that only know a single name still work, and rows written before
   * the split keep reading correctly.
   */
  firstName: z.string().min(1).max(60).optional(),
  lastName: z.string().min(1).max(60).optional(),
  name: z.string().min(2).max(120).optional(),
  /** Per person, not per account — who to ring about a delivery. */
  phone: z.string().max(40).nullish(),
}).refine(
  (u) => (u.name?.trim().length ?? 0) >= 2
      || ((u.firstName?.trim().length ?? 0) >= 1 && (u.lastName?.trim().length ?? 0) >= 1),
  { message: 'A first and last name are required', path: ['firstName'] },
);

/**
 * Creating a login on a trade customer's account, from the Miraki side.
 *
 * `password` rather than an invite link, for the same reason `zCreateUser` takes one: the
 * invitation path needs Notifications, and until then somebody reads a password down the
 * phone. The service forces a change at first sign-in, so the one typed here stops working
 * the moment the customer is in.
 *
 * `roleIds` is required, not optional. A login with no roles signs in and sees nothing, which
 * is a legitimate state to END UP in but never one to deliberately create — and for a first
 * login the service refuses it outright, because an account whose only user cannot administer
 * it has nobody who can fix that.
 */
export const zCreateRetailerUser = z.object({
  retailerId: zId,
  firstName: z.string().min(1).max(60),
  lastName: z.string().min(1).max(60),
  phone: z.string().max(40).optional(),
  email: z.string().email().max(254),
  password: z.string().min(12).max(200),
  roleIds: z.array(zId).min(1, 'choose at least one role'),
});

export const zAuditQuery = z.object({
  action: z.string().max(64).optional(),
  actorId: zId.optional(),
  objectType: z.string().max(64).optional(),
  objectId: z.string().max(64).optional(),
  /**
   * AU-04 — free text across the columns a person actually remembers: who did it, what the
   * action was called, and what it was done to.
   *
   * Server-side, deliberately. The screen shows the most recent `limit` rows, so a filter
   * applied in the browser can only search what already arrived — type a retailer's name and it
   * finds nothing unless their row happened to be in the last fifty. That is worse than no
   * search, because it answers "nothing matched" when the truth is "nothing matched recently".
   */
  q: z.string().trim().min(1).max(120).optional(),
  /** AU-05 — a window, so "what happened on the day of the complaint" is answerable. */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export type Audience = z.infer<typeof zAudience>;
export type CreateRole = z.infer<typeof zCreateRole>;
export type UpdateRole = z.infer<typeof zUpdateRole>;
export type CreateUser = z.infer<typeof zCreateUser>;
export type UpdateUser = z.infer<typeof zUpdateUser>;
export type UpdateRetailerUser = z.infer<typeof zUpdateRetailerUser>;
export type CreateRetailerUser = z.infer<typeof zCreateRetailerUser>;

/**
 * D47, CR-02 — a language an administrator adds, and the strings behind it.
 *
 * `direction` is deliberately absent: it is derived from the code, because there
 * is one right answer per language and the wrong one renders an unreadable page.
 */
export const zLocaleCode = z.string().min(2).max(12).regex(
  /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})?$/,
  'a BCP-47 tag: en, ar, pt-BR',
);

export const zCreateLocale = z.object({
  code: zLocaleCode,
  name: z.string().min(2).max(60),
  /** The language's name in itself — العربية. What the switcher shows. */
  endonym: z.string().min(1).max(60),
});

export const zUpdateLocale = z.object({
  name: z.string().min(2).max(60).optional(),
  endonym: z.string().min(1).max(60).optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(999).optional(),
}).refine((o) => Object.keys(o).length > 0, 'no fields to update');

/** Partial: the editor saves the keys on screen. A blank value clears one. */
export const zSetMessages = z.object({
  messages: z.record(z.string().max(200), z.string().max(4000)),
});

export type CreateLocale = z.infer<typeof zCreateLocale>;
